/**
 * The Amazon Connect phone, reduced to what the Dialer needs.
 *
 * == Where the phone actually is ==========================================
 *
 * Amazon Connect's own contact control panel runs in an iframe from the
 * instance's domain and owns the call: sign-in, the microphone, WebRTC
 * audio, the connection to the phone network. This page never touches
 * audio. It drives that panel through Amazon Connect Streams, which is
 * served from this site as a pinned, integrity-checked copy
 * (public/lib/amazon-connect-streams/<version>/SOURCE.md), not fetched from
 * a CDN and not installed as a package.
 *
 * == Why an adapter ======================================================
 *
 * Streams is a global with callback-style methods and no types this
 * project compiles. Everything the page uses goes through the handful of
 * functions below, which turn callbacks into promises and Connect's
 * vocabulary into the page's, so the page can be tested against a fake and
 * a Streams upgrade has one file to check.
 */

export const STREAMS_SCRIPT = Object.freeze({
  version: '2.30.0',
  src: '/lib/amazon-connect-streams/2.30.0/connect-streams-min.js',
  integrity: 'sha384-XDSEys9gAix2BmOAZPn3qDy4xBpT1NRkQQpJfD+Q9tnyWbBSGlNfNd0K8NlVFsPq',
});

export type ConnectGlobal = any;

let loading: Promise<ConnectGlobal> | null = null;

const connectOnWindow = (): ConnectGlobal | null => {
  const c = (window as any).connect;
  return c && c.core && typeof c.core.initCCP === 'function' ? c : null;
};

/** Load Streams once per page. Resolves with the `connect` global. */
export function loadStreams(doc: Document = document): Promise<ConnectGlobal> {
  const ready = connectOnWindow();
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = doc.createElement('script');
    s.src = STREAMS_SCRIPT.src;
    s.integrity = STREAMS_SCRIPT.integrity;
    s.crossOrigin = 'anonymous';
    s.async = true;
    s.onload = () => {
      const c = connectOnWindow();
      if (c) resolve(c);
      else { loading = null; reject(new Error('STREAMS_MISSING')); }
    };
    s.onerror = () => { loading = null; reject(new Error('STREAMS_LOAD_FAILED')); };
    doc.head.appendChild(s);
  });
  return loading;
}

/** Test seam: forget a previous load. */
export function resetStreamsLoaderForTests(): void { loading = null; }

// ── The page's vocabulary ──────────────────────────────────────────────

export interface AgentView {
  /** The status Connect shows, e.g. "Available", "Offline", "Busy". */
  stateName: string;
  /** Whether incoming calls can ring this person (their chosen status is routable). */
  takingCalls: boolean;
}

export type CallStatus = 'incoming' | 'dialing' | 'connected' | 'on-hold' | 'ended' | 'missed' | 'failed';

export interface CallView {
  id: string;
  inbound: boolean;
  number: string | null;
  status: CallStatus;
  /** A colleague brought onto this call, while they are on it. */
  transfer: TransferView | null;
}

/** A colleague on Connect's transfer list (a quick connect of type agent). */
export interface Colleague {
  id: string;
  name: string;
}

export interface TransferView {
  name: string;
  /** ringing: not answered yet; talking: the caller is on hold while you talk; joined: all three on the line. */
  stage: 'ringing' | 'talking' | 'joined' | 'on-hold';
}

export type PhoneProblem = 'AUTH_REQUIRED' | 'ACCESS_DENIED' | 'UNREACHABLE' | 'MICROPHONE' | 'BROWSER' | 'CALL_FAILED';

export interface PhoneEvents {
  onAgent(agent: AgentView): void;
  onCall(call: CallView | null): void;
  onMute(muted: boolean): void;
  onProblem(problem: PhoneProblem, detail?: string): void;
}

export interface PhoneController {
  dial(e164: string): Promise<void>;
  hangUp(): Promise<void>;
  setMuted(muted: boolean): void;
  setHold(onHold: boolean): Promise<void>;
  sendDigit(digit: string): Promise<void>;
  answer(): Promise<void>;
  decline(): Promise<void>;
  /** Close out an ended, missed or failed call so the next one can start. */
  finish(): Promise<void>;
  setTakingCalls(on: boolean): Promise<void>;
  /** Colleagues this person can transfer to, from Connect's transfer list. Never includes themself. */
  colleagues(): Promise<Colleague[]>;
  /** Ring a colleague. The caller is put on hold by Connect while they answer. */
  transferTo(colleagueId: string): Promise<void>;
  /** Leave the call: the caller stays with the colleague. */
  completeTransfer(): Promise<void>;
  /** Put the caller, the colleague and you on one call. */
  joinAll(): Promise<void>;
  /** Drop the colleague and take the caller off hold. */
  cancelTransfer(): Promise<void>;
  /** Stop the embedded panel (and its retries), e.g. when the phone moves to its own window. */
  stop(): void;
}

export interface LineForPhone {
  ccpUrl: string;
  region: string;
}

/** A Streams failure arrives as a JSON string, an object or nothing. Say something a person can use. */
export function failureText(e: unknown): string {
  let v: any = e;
  if (typeof e === 'string') {
    try { v = JSON.parse(e); } catch { return e || 'The phone line refused the request.'; }
  }
  const text = v?.message || v?.errorMessage || v?.type || v?.errorType;
  return typeof text === 'string' && text ? text : 'The phone line refused the request.';
}

const viaCallbacks = (fn: (cbs: { success: () => void; failure: (e: unknown) => void }) => void): Promise<void> =>
  new Promise((resolve, reject) => {
    try {
      fn({ success: () => resolve(), failure: (e) => reject(new Error(failureText(e))) });
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });

function statusOf(contact: any): CallStatus {
  const type = String(contact.getStatus?.()?.type || '').toLowerCase();
  const inbound = !!contact.isInbound?.();
  switch (type) {
    case 'incoming': return 'incoming';
    case 'connecting':
    case 'pending':
    case 'init':
      return inbound ? 'incoming' : 'dialing';
    case 'connected': {
      const conn = contact.getInitialConnection?.();
      return conn?.isOnHold?.() ? 'on-hold' : 'connected';
    }
    case 'missed': return 'missed';
    case 'error':
    case 'rejected':
      return 'failed';
    case 'ended':
    default:
      return 'ended';
  }
}

function connType(conn: any): string {
  return String(conn?.getStatus?.()?.type || '').toLowerCase();
}

function liveThirdParty(contact: any): any | null {
  const list: any[] = contact.getThirdPartyConnections?.() || [];
  return list.find((c) => !['disconnected', 'ended', 'error'].includes(connType(c))) || null;
}

function transferOf(contact: any, names: Map<string, string>): TransferView | null {
  const third = liveThirdParty(contact);
  if (!third) return null;
  const id = String(contact.getContactId?.() || '');
  const endpoint = third.getEndpoint?.() || {};
  const name = names.get(id) || endpoint.name || endpoint.phoneNumber || 'Colleague';
  const t = connType(third);
  if (t === 'hold' || third.isOnHold?.()) return { name, stage: 'on-hold' };
  if (t !== 'connected') return { name, stage: 'ringing' };
  const caller = contact.getInitialConnection?.();
  const callerHeld = connType(caller) === 'hold' || !!caller?.isOnHold?.();
  return { name, stage: callerHeld ? 'talking' : 'joined' };
}

function viewOf(contact: any, names: Map<string, string>): CallView {
  const conn = contact.getInitialConnection?.();
  const endpoint = conn?.getEndpoint?.();
  return {
    id: String(contact.getContactId?.() || contact.contactId || ''),
    inbound: !!contact.isInbound?.(),
    number: endpoint?.phoneNumber || null,
    status: statusOf(contact),
    transfer: transferOf(contact, names),
  };
}

function isVoice(contact: any): boolean {
  const t = contact.getType?.();
  return !t || String(t).toLowerCase() === 'voice';
}

function agentViewOf(agent: any): AgentView {
  const current = agent.getState?.() || {};
  const chosen = typeof agent.getAvailabilityState === 'function' ? agent.getAvailabilityState() : current;
  return {
    stateName: String(current.name || chosen?.name || 'Unknown'),
    takingCalls: String(chosen?.type || '').toLowerCase() === 'routable',
  };
}

/**
 * Start the phone in `container` and wire its events. Call once per page.
 * Sign-in happens in Amazon Connect's own window; see the page for what a
 * person is told when that window is blocked.
 */
export function startPhone(connect: ConnectGlobal, container: HTMLElement, line: LineForPhone, events: PhoneEvents): PhoneController {
  let agentRef: any = null;
  let current: any = null;
  // Transfer targets as Connect returned them, and who each call was sent to.
  const endpoints = new Map<string, any>();
  const transferNames = new Map<string, string>();

  connect.core.initCCP(container, {
    ccpUrl: line.ccpUrl,
    region: line.region,
    loginPopup: true,
    loginPopupAutoClose: true,
    loginOptions: { autoClose: true, height: 640, width: 420, top: 80, left: 80 },
    softphone: { allowFramedSoftphone: true, disableRingtone: false },
    pageOptions: { enableAudioDeviceSettings: true, enablePhoneTypeSettings: false },
    storageAccess: { canRequest: true, mode: 'default' },
    ccpAckTimeout: 5000,
    ccpSynTimeout: 3000,
    ccpLoadTimeout: 10000,
  });

  connect.core.onAuthFail?.(() => events.onProblem('AUTH_REQUIRED'));
  connect.core.onAccessDenied?.(() => events.onProblem('ACCESS_DENIED'));
  connect.core.onIframeRetriesExhausted?.(() => events.onProblem('UNREACHABLE'));

  connect.agent((agent: any) => {
    agentRef = agent;
    events.onAgent(agentViewOf(agent));
    agent.onStateChange?.(() => events.onAgent(agentViewOf(agent)));
    agent.onRefresh?.(() => events.onAgent(agentViewOf(agent)));
    agent.onMuteToggle?.((e: any) => events.onMute(!!e?.muted));
    agent.onSoftphoneError?.((e: any) => {
      const t = String(e?.errorType || '').toLowerCase();
      if (t === 'microphone_not_shared') events.onProblem('MICROPHONE');
      else if (t === 'unsupported_browser') events.onProblem('BROWSER');
      else events.onProblem('CALL_FAILED', failureText(e));
    });
  });

  connect.contact((contact: any) => {
    if (!isVoice(contact)) return;
    current = contact;
    const emit = () => { if (current === contact) events.onCall(viewOf(contact, transferNames)); };
    for (const hook of ['onIncoming', 'onConnecting', 'onAccepted', 'onConnected', 'onRefresh', 'onEnded', 'onMissed', 'onError']) {
      contact[hook]?.(emit);
    }
    contact.onDestroy?.(() => {
      if (current === contact) { current = null; events.onCall(null); }
    });
    emit();
  });

  const need = <T,>(v: T | null, what: string): T => {
    if (!v) throw new Error(what);
    return v;
  };

  return {
    dial: (e164) => viaCallbacks((cbs) => {
      const agent = need(agentRef, 'The phone is not signed in yet.');
      agent.connect(connect.Endpoint.byPhoneNumber(e164), cbs);
    }),
    hangUp: () => viaCallbacks((cbs) => {
      const c = need(current, 'There is no call to end.');
      (c.getAgentConnection?.() || c.getInitialConnection()).destroy(cbs);
    }),
    setMuted: (muted) => {
      const agent = need(agentRef, 'The phone is not signed in yet.');
      if (muted) agent.mute(); else agent.unmute();
    },
    setHold: (onHold) => viaCallbacks((cbs) => {
      const conn = need(current, 'There is no call to hold.').getInitialConnection();
      if (onHold) conn.hold(cbs); else conn.resume(cbs);
    }),
    sendDigit: (digit) => viaCallbacks((cbs) => {
      const c = need(current, 'There is no call to send tones to.');
      (c.getAgentConnection?.() || c.getInitialConnection()).sendDigits(digit, cbs);
    }),
    answer: () => viaCallbacks((cbs) => need(current, 'There is no call to answer.').accept(cbs)),
    decline: () => viaCallbacks((cbs) => {
      const c = need(current, 'There is no call to decline.');
      if (typeof c.reject === 'function') c.reject(cbs);
      else (c.getAgentConnection?.() || c.getInitialConnection()).destroy(cbs);
    }),
    finish: async () => {
      const c = current;
      if (!c) return;
      if (typeof c.clear === 'function') await viaCallbacks((cbs) => c.clear(cbs));
      if (current === c) { current = null; events.onCall(null); }
    },
    setTakingCalls: (on) => viaCallbacks((cbs) => {
      const agent = need(agentRef, 'The phone is not signed in yet.');
      const want = on ? 'routable' : 'offline';
      const state = (agent.getAgentStates?.() || []).find((s: any) => String(s.type).toLowerCase() === want);
      if (!state) throw new Error(on ? 'No "Available" status is set up for your phone.' : 'No "Offline" status is set up for your phone.');
      agent.setState(state, cbs, { enqueueNextState: true });
    }),
    colleagues: () => new Promise<Colleague[]>((resolve, reject) => {
      try {
        const agent = need(agentRef, 'The phone is not signed in yet.');
        const me = String(agent.getName?.() || '').trim().toLowerCase();
        agent.getEndpoints(agent.getAllQueueARNs(), {
          success: (data: any) => {
            endpoints.clear();
            const list: Colleague[] = [];
            for (const e of data?.endpoints || []) {
              if (String(e.type).toLowerCase() !== 'agent') continue;
              const name = String(e.name || '').trim();
              if (!name || name.toLowerCase() === me) continue;
              const id = String(e.endpointARN || e.endpointId || name);
              endpoints.set(id, e);
              list.push({ id, name });
            }
            list.sort((a, b) => a.name.localeCompare(b.name));
            resolve(list);
          },
          failure: (e: unknown) => reject(new Error(failureText(e))),
        });
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    }),
    transferTo: (colleagueId) => viaCallbacks((cbs) => {
      const c = need(current, 'There is no call to transfer.');
      if (liveThirdParty(c)) throw new Error('A colleague is already on this call.');
      const endpoint = need(endpoints.get(colleagueId) || null, 'That colleague is no longer on the transfer list.');
      transferNames.set(String(c.getContactId?.() || ''), String(endpoint.name || 'Colleague'));
      c.addConnection(endpoint, cbs);
    }),
    completeTransfer: () => viaCallbacks((cbs) => {
      const c = need(current, 'There is no call to transfer.');
      need(liveThirdParty(c), 'No colleague is on this call yet.');
      need(c.getAgentConnection?.() || null, 'Your side of the call is already gone.').destroy(cbs);
    }),
    joinAll: () => viaCallbacks((cbs) => {
      const c = need(current, 'There is no call to join.');
      need(liveThirdParty(c), 'No colleague is on this call yet.');
      c.conferenceConnections(cbs);
    }),
    cancelTransfer: async () => {
      const c = need(current, 'There is no transfer to cancel.');
      const third = need(liveThirdParty(c), 'No colleague is on this call.');
      await viaCallbacks((cbs) => third.destroy(cbs));
      const caller = c.getInitialConnection?.();
      if (caller && (connType(caller) === 'hold' || caller.isOnHold?.())) {
        await viaCallbacks((cbs) => caller.resume(cbs));
      }
    },
    stop: () => {
      try { connect.core.terminate?.(); } catch { /* already gone */ }
    },
  };
}

/**
 * Is this Brave? Its Shields block the cookie the embedded panel needs,
 * even after a successful Connect sign-in (seen 2026-09-16). `navigator.brave`
 * exists only in Brave.
 */
export function isBraveBrowser(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  return !!nav && 'brave' in (nav as unknown as Record<string, unknown>);
}
